// Travas e prévia do envio de cobrança por WhatsApp (BoletoEmitidoModal).
//
// Por que existe: em 17/08/2026 o Condomínio Portal dos Buritis recebeu as
// quatro faturas de JUL/2026 (R$ 5.212,96) **sem código de barras e sem PIX** —
// o cliente ligou no dia do vencimento sem ter como pagar. A tela de envio
// validava só telefone e e-mail; os campos "Código PIX" e "Linha Digitável"
// podiam ir vazios e o webhook disparava do mesmo jeito. As quatro tiveram de
// ser canceladas e reemitidas (motivo gravado: "sem codigo de barras").
//
// Duas peças, as duas puras (sem I/O nem JSX) para rodar sob `node --test`:
//
//   checarEnvioCobranca  -> lista de problemas, cada um 'bloqueio' ou 'alerta'.
//                           Bloqueio trava o botão; alerta só avisa.
//   montarPreviaEnvio    -> os valores JÁ FORMATADOS como o cliente vai ver.
//
// ⚠️ A prévia precisa espelhar o nó Code "Trata os dados" do workflow n8n
// `05. [FINANCEIRO] Envio de cobrança [UaisyChat]` (id a2P4r3gmN5JpI7M6). É lá
// que o valor ganha vírgula, a data vira DD/MM/AAAA e o telefone recebe o 55.
// Se a prévia formatar diferente do n8n, ela deixa de ser prévia e vira enfeite
// — mexeu num, confira o outro.

/** Bloqueio impede o envio; alerta é só aviso na tela. */
export type SeveridadeChecagem = 'bloqueio' | 'alerta';

export interface ChecagemEnvio {
  /** Campo da tela ao qual o problema se refere (usado para destacar o input). */
  campo:
    | 'telefone'
    | 'telefone2'
    | 'email'
    /** Nenhum canal escolhido — destaca o telefone e o e-mail ao mesmo tempo. */
    | 'canalEnvio'
    | 'nome'
    | 'mesReferencia'
    | 'valor'
    | 'vencimento'
    | 'formaPagamento'
    | 'linhaDigitavel'
    | 'codigoPix'
    | 'duplicata'
    /** Boleto da parceira sem o PDF unificado — ver pdfUnificadoGran.ts. */
    | 'pdfUnificado';
  severidade: SeveridadeChecagem;
  mensagem: string;
}

export interface DadosEnvioCobranca {
  nomeCliente: string;
  telefone: string;
  telefone2: string;
  email: string;
  mesReferencia: string;
  /** Vem do input `type="number"`, então é string ('' quando vazio). */
  valorBoleto: string;
  /** ISO `AAAA-MM-DD`, como sai do input `type="date"`. */
  vencimento: string;
  codigoPix: string;
  linhaDigitavel: string;
  /** UC marcada para receber a cobrança também por e-mail. */
  receberEmail: boolean;
  /** `faturas.bloqueado_envio_duplicata` — duplicata pendente de resolução. */
  bloqueadoDuplicata?: boolean | null;
  /** Cobrança da parceira criada sem o PDF unificado (`faltaPdfUnificado`). */
  faltaPdfUnificado?: boolean;
}

/** Mesmo mínimo que a tela já usava antes destas travas. */
export const TELEFONE_MIN_DIGITOS = 10;

/** Linha digitável de boleto é sempre 47 dígitos — conferido na base em
 *  17/08/2026: 5.342 faturas com linha digitável, **todas** com 47, tanto as do
 *  Sicoob (Vigor) quanto as do Asaas (parceiras). Um número mais curto é campo
 *  truncado no meio do caminho, não uma variação legítima de layout. */
export const LINHA_DIGITAVEL_DIGITOS = 47;

/** Todo payload PIX copia-e-cola (EMV, padrão BCB) abre com o Payload Format
 *  Indicator `000201`. Mesma conferência: 3.333 faturas com PIX preenchido,
 *  3.333 começando com `000201`. */
const PIX_EMV_PREFIXO = '000201';

const somenteDigitos = (valor: string | null | undefined) => String(valor ?? '').replace(/\D/g, '');

/** Aceita o que o WhatsApp aceita; não tenta validar domínio de verdade. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * O e-mail só vale como canal de envio quando a UC consentiu
 * (`receber_cobranca_email`) e o endereço é plausível — é a flag que a edge
 * function `enviar-cobranca-email` relê no servidor antes de disparar. Sem ela
 * o envio voltaria como `opt_out` e ninguém receberia nada.
 *
 * Exportada porque a tela de comunicação (fatura que pulou o cálculo) faz a
 * mesma pergunta fora de `checarEnvioCobranca`.
 */
export const emailServeComoCanal = (
  receberEmail: boolean,
  email: string | null | undefined,
): boolean => {
  const limpo = String(email ?? '').trim();
  return receberEmail && !!limpo && EMAIL_RE.test(limpo);
};

/**
 * Roda todas as travas do envio de cobrança.
 *
 * `hoje` é injetável só para o teste — em produção fica o padrão.
 */
export function checarEnvioCobranca(
  dados: DadosEnvioCobranca,
  hoje: Date = new Date(),
): ChecagemEnvio[] {
  const problemas: ChecagemEnvio[] = [];

  // ── Duplicata: trava que já existia, movida para cá para ficar tudo num lugar
  if (dados.bloqueadoDuplicata) {
    problemas.push({
      campo: 'duplicata',
      severidade: 'bloqueio',
      mensagem:
        'Esta fatura tem uma duplicata pendente. Resolva a duplicata no painel de faturas antes de enviar.',
    });
  }

  // ── Boleto da parceira sem o PDF unificado: o link do botão "Ver boleto" no
  //    WhatsApp (token_boleto_publico) só nasce junto com o PDF. Sem ele o cliente
  //    receberia a cobrança sem o boleto unificado (caso da fatura 14510, 20/10/2026).
  if (dados.faltaPdfUnificado) {
    problemas.push({
      campo: 'pdfUnificado',
      severidade: 'bloqueio',
      mensagem:
        'O PDF unificado deste boleto não foi gerado. Volte e clique em "Gerar PDF unificado" antes de enviar.',
    });
  }

  // ── Destinatário: WhatsApp OU e-mail, não mais só WhatsApp.
  //
  // Antes o telefone era obrigatório sempre, e clientes que só aceitam e-mail
  // (Prefeitura Municipal de Morrinhos, 161 UCs, nenhuma com WhatsApp
  // cadastrado) simplesmente não conseguiam ser cobrados: o botão ficava
  // travado num campo que nunca ia ser preenchido. Agora basta um canal
  // utilizável — e o e-mail só conta como canal quando a UC está marcada com
  // `receber_cobranca_email`, porque é essa flag que autoriza o disparo pela
  // edge function `enviar-cobranca-email`.
  const tel1 = somenteDigitos(dados.telefone);
  const telefonePreenchido = tel1.length > 0;
  const telefoneValido = tel1.length >= TELEFONE_MIN_DIGITOS;

  const email = dados.email.trim();
  const emailValido = emailServeComoCanal(dados.receberEmail, email);

  // Número começado e não terminado é erro de digitação, não escolha de canal:
  // bloqueia mesmo que o e-mail já garanta o envio, senão o operador acha que
  // mandou para os dois canais e o WhatsApp foi descartado em silêncio.
  if (telefonePreenchido && !telefoneValido) {
    problemas.push({
      campo: 'telefone',
      severidade: 'bloqueio',
      mensagem: 'O Telefone (WhatsApp) está incompleto. Complete o número ou apague o campo.',
    });
  }

  if (!telefoneValido && !emailValido) {
    problemas.push({
      campo: dados.receberEmail ? 'canalEnvio' : 'telefone',
      severidade: 'bloqueio',
      mensagem: dados.receberEmail
        ? 'Escolha por onde enviar: preencha o Telefone (WhatsApp) ou o e-mail do cliente.'
        : 'Telefone (WhatsApp) é obrigatório para enviar a cobrança.',
    });
  }

  const tel2 = somenteDigitos(dados.telefone2);
  if (tel2.length > 0 && tel2.length < TELEFONE_MIN_DIGITOS) {
    problemas.push({
      campo: 'telefone2',
      severidade: 'bloqueio',
      mensagem: 'O 2º WhatsApp está incompleto. Complete o número ou apague o campo.',
    });
  }

  if (email && !EMAIL_RE.test(email)) {
    // Alerta quando ainda há WhatsApp (a cobrança sai por lá de qualquer
    // forma); com o e-mail sendo o único canal, o bloqueio acima já pegou.
    problemas.push({
      campo: 'email',
      severidade: 'alerta',
      mensagem: 'O e-mail informado parece inválido — confira antes de enviar.',
    });
  } else if (dados.receberEmail && !email && telefoneValido) {
    problemas.push({
      campo: 'email',
      severidade: 'alerta',
      mensagem: 'Esta UC recebe cobrança por e-mail, mas o campo está vazio — vai só por WhatsApp.',
    });
  }

  // ── Forma de pagamento: a trava que faltava, e o motivo desta função existir.
  //    A mensagem chega ao cliente com o PIX e a linha digitável no corpo; sem
  //    nenhum dos dois ela é só um aviso de dívida sem como pagar.
  const linha = somenteDigitos(dados.linhaDigitavel);
  const pix = dados.codigoPix.trim();

  if (!linha && !pix) {
    problemas.push({
      campo: 'formaPagamento',
      severidade: 'bloqueio',
      mensagem:
        'Sem código de barras e sem PIX: o cliente receberia a cobrança sem nenhuma forma de pagar. Emita ou reemita o boleto antes de enviar.',
    });
  } else {
    if (!linha) {
      problemas.push({
        campo: 'linhaDigitavel',
        severidade: 'alerta',
        mensagem: 'Vai sem código de barras — o cliente só vai conseguir pagar pelo PIX.',
      });
    }
    if (!pix) {
      problemas.push({
        campo: 'codigoPix',
        severidade: 'alerta',
        mensagem: 'Vai sem PIX — o cliente só vai conseguir pagar pelo código de barras.',
      });
    }
  }

  if (linha && linha.length !== LINHA_DIGITAVEL_DIGITOS) {
    problemas.push({
      campo: 'linhaDigitavel',
      severidade: 'bloqueio',
      mensagem: `Linha digitável com ${linha.length} dígitos — o correto são ${LINHA_DIGITAVEL_DIGITOS}. Copie de novo do boleto.`,
    });
  }

  if (pix && !pix.startsWith(PIX_EMV_PREFIXO)) {
    problemas.push({
      campo: 'codigoPix',
      severidade: 'bloqueio',
      mensagem: 'O código PIX não parece um copia-e-cola válido (deve começar com 000201).',
    });
  }

  // ── Dados que compõem a mensagem
  if (!dados.nomeCliente.trim()) {
    problemas.push({
      campo: 'nome',
      severidade: 'bloqueio',
      mensagem: 'Nome do cliente é obrigatório — ele aparece na mensagem.',
    });
  }

  if (!dados.mesReferencia.trim()) {
    problemas.push({
      campo: 'mesReferencia',
      severidade: 'bloqueio',
      mensagem: 'Mês de referência é obrigatório — sem ele o cliente não sabe qual fatura é.',
    });
  }

  const valor = Number(dados.valorBoleto);
  if (!dados.valorBoleto.trim() || !Number.isFinite(valor) || valor <= 0) {
    problemas.push({
      campo: 'valor',
      severidade: 'bloqueio',
      mensagem: 'Valor do boleto precisa ser maior que zero.',
    });
  }

  if (!dados.vencimento) {
    problemas.push({
      campo: 'vencimento',
      severidade: 'bloqueio',
      mensagem: 'Vencimento do boleto é obrigatório.',
    });
  } else if (vencimentoNoPassado(dados.vencimento, hoje)) {
    // Alerta, não bloqueio: reenviar cobrança já vencida é rotina da régua.
    problemas.push({
      campo: 'vencimento',
      severidade: 'alerta',
      mensagem: 'O vencimento já passou — o cliente vai receber uma cobrança vencida.',
    });
  }

  return problemas;
}

/** Compara as datas em horário local, sem fuso: `vencimento` é `AAAA-MM-DD` e
 *  `new Date('2026-08-17')` seria interpretado como UTC, o que empurra o dia
 *  para trás no Brasil e faria a cobrança de hoje aparecer como vencida. */
function vencimentoNoPassado(vencimento: string, hoje: Date): boolean {
  const partes = vencimento.split('-');
  if (partes.length !== 3) return false;
  const [ano, mes, dia] = partes.map(Number);
  if (!ano || !mes || !dia) return false;
  const venc = new Date(ano, mes - 1, dia);
  const referencia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return venc.getTime() < referencia.getTime();
}

export const temBloqueio = (problemas: ChecagemEnvio[]) =>
  problemas.some(p => p.severidade === 'bloqueio');

// ─────────────────────────── prévia do envio ───────────────────────────

export interface PreviaEnvioCobranca {
  /** Números como o UaisyChat recebe: só dígitos, com 55 na frente, sem repetidos. */
  destinatarios: string[];
  nome: string;
  uc: string;
  mesReferencia: string;
  /** `DD/MM/AAAA`, como o n8n converte. */
  vencimento: string;
  /** `1234,56` — vírgula decimal, sem separador de milhar (igual ao n8n). */
  valor: string;
  codigoPix: string;
  linhaDigitavel: string;
  /** Destinos do e-mail; vazio quando a UC não pediu cobrança por e-mail. */
  emails: string[];
}

/** Mesma normalização do nó "Trata os dados" no n8n. */
const normalizaTelefone = (valor: string): string | null => {
  const digitos = somenteDigitos(valor);
  if (!digitos) return null;
  return digitos.startsWith('55') ? digitos : `55${digitos}`;
};

/** Monta o que o cliente vai receber, já formatado. */
export function montarPreviaEnvio(
  dados: DadosEnvioCobranca,
  ucCodigo?: string | null,
): PreviaEnvioCobranca {
  // Ordem igual à do envio real: a tela descarta o que tem menos de 10 dígitos
  // (montarTelefonesEnvio) e só então o n8n prefixa o 55 e deduplica. Filtrar
  // por tamanho DEPOIS do prefixo descartaria número de DDD 55 (Rio Grande do
  // Sul), que o n8n deixa passar intacto — a prévia mostraria um destinatário
  // a menos do que a mensagem que de fato sai.
  const destinatarios = Array.from(
    new Set(
      [dados.telefone, dados.telefone2]
        .filter(t => somenteDigitos(t).length >= TELEFONE_MIN_DIGITOS)
        .map(normalizaTelefone)
        .filter((t): t is string => !!t),
    ),
  );

  const valorNumero = Number(dados.valorBoleto);
  const valor =
    dados.valorBoleto.trim() && Number.isFinite(valorNumero)
      ? valorNumero.toFixed(2).replace('.', ',')
      : '';

  const partes = dados.vencimento.split('-');
  const vencimento = partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : '';

  const email = dados.email.trim();

  return {
    destinatarios,
    nome: dados.nomeCliente.trim(),
    uc: String(ucCodigo ?? ''),
    mesReferencia: dados.mesReferencia.trim(),
    vencimento,
    valor,
    codigoPix: dados.codigoPix.trim(),
    linhaDigitavel: dados.linhaDigitavel.trim(),
    emails: dados.receberEmail && email ? [email] : [],
  };
}
